import { PermissionName } from '@sironamedical/common';
import { useValidateUserHasRbacPermissions } from 'hooks/useValidateUserHasRbacPermissions';
import { useMutation } from '@apollo/client';
import { FF, useFeatureFlagEnabled } from 'modules/feature-flags';
import { useCallback, useMemo } from 'react';
import { useCurrentCase } from 'hooks/useCurrentCase';
import { WithApolloContext } from 'modules/Apollo/types';
import { useReportLockedState } from 'domains/reporter/Reporter/hooks/useReportLockedState';
import { SET_REPORT_DISCREPANCY } from 'modules/Apollo/queries';
import { useToasterDispatch } from 'common/ui/Toaster';
import { NameSpaces, sendCrossTabEvent } from 'modules/EventsManager';
import { PageTypes } from 'utils/pageTypes';
import { logger } from 'modules/logger';
import { useRecorderVoiceCommandEventsListener } from 'domains/reporter/Reporter/hooks/useRecorderVoiceCommandEventsListener';
import {
  PROVISIONAL_SUBMIT_STATUSES,
  type ReportStatuses,
  reportStatusState,
} from 'domains/reporter/Reporter/state';
import { useRecoilValue } from 'recoil';

type UseMarkAsDiscrepantReturn = {
  shouldRenderMarkAsDiscrepantToggle: boolean;
  canMarkAsDiscrepant: boolean;
  toggleMarkAsDiscrepant: (checked: boolean) => Promise<void>;
  isDiscrepancyFlagged: boolean;
  isDisabled: boolean;
  isSettingDiscrepancy: boolean;
};

type SetReportDiscrepancyResponse = {
  setReportDiscrepancy: {
    __typename?: 'Report';
    smid: string;
    isDiscrepancyFlagged?: boolean | null;
  };
};

type SetReportDiscrepancyVariables = {
  smid: string;
  isDiscrepancyFlagged: boolean;
};

export const useMarkAsDiscrepant = ({ context }: WithApolloContext): UseMarkAsDiscrepantReturn => {
  const [isMarkAsDiscrepantEnabled] = useFeatureFlagEnabled(FF.REPORTER_MARK_AS_DISCREPANT);

  const { currentCase } = useCurrentCase();
  const worklistSmid = currentCase?.smid;
  const { isSubmittedStatus, isClaimedByMe } = useReportLockedState({ context });
  const { enqueueToast } = useToasterDispatch();
  const reportStatus = useRecoilValue(reportStatusState);

  const hasMarkAsDiscrepantPermission = useValidateUserHasRbacPermissions({
    permissionNames: [PermissionName.AllowMarkAsDiscrepant],
  });

  const [setReportDiscrepancyMutation, { loading: isSettingDiscrepancy }] = useMutation<
    SetReportDiscrepancyResponse,
    SetReportDiscrepancyVariables
  >(SET_REPORT_DISCREPANCY, { context });

  const canMarkAsDiscrepant =
    isMarkAsDiscrepantEnabled && hasMarkAsDiscrepantPermission && isClaimedByMe;

  const isDiscrepancyFlagged = currentCase?.report?.isDiscrepancyFlagged ?? false;
  const isDisabled =
    isSettingDiscrepancy ||
    (PROVISIONAL_SUBMIT_STATUSES as readonly ReportStatuses[]).includes(reportStatus);
  const shouldRenderMarkAsDiscrepantToggle = canMarkAsDiscrepant && !isSubmittedStatus;
  const shouldIgnoreVoiceCommands =
    !canMarkAsDiscrepant || !shouldRenderMarkAsDiscrepantToggle || isDisabled;

  const toggleMarkAsDiscrepant = useCallback(
    async (checked: boolean) => {
      const reportSmid = currentCase?.report?.smid;

      if (!reportSmid || !canMarkAsDiscrepant) {
        logger.warn(
          '[useMarkAsDiscrepant] Attempted to toggle discrepancy without permission or report smid',
          { reportSmid, canMarkAsDiscrepant }
        );
        return;
      }

      await setReportDiscrepancyMutation({
        variables: { smid: reportSmid, isDiscrepancyFlagged: checked },
        optimisticResponse: {
          setReportDiscrepancy: {
            __typename: 'Report',
            smid: reportSmid,
            isDiscrepancyFlagged: checked,
          },
        },

        onCompleted: (data) => {
          const isFlagged = data?.setReportDiscrepancy?.isDiscrepancyFlagged ?? checked;
          enqueueToast(
            `Discrepancy ${isFlagged ? 'flagged' : 'cleared'} for exam ${worklistSmid}`,
            { severity: 'default', position: 'top-right', icon: 'greenCheck' }
          );
          logger.info(
            `[useMarkAsDiscrepant] Discrepancy ${isFlagged ? 'flagged' : 'cleared'} for exam ${worklistSmid}`
          );

          if (worklistSmid) {
            // allows the status icons to update in worklist tab
            sendCrossTabEvent(NameSpaces.WORKLIST_ITEM_PATCH, {
              type: 'worklistItemPatch',
              targetPage: PageTypes.WORKLIST,
              data: {
                id: worklistSmid,
                patch: {
                  report: {
                    smid: reportSmid,
                    isDiscrepancyFlagged: isFlagged,
                  },
                },
              },
            });
          }
        },
        onError: (error) => {
          enqueueToast(`Failed to update discrepancy status for exam ${worklistSmid}`, {
            severity: 'error',
          });
          logger.error(
            `[useMarkAsDiscrepant] Failed to update discrepancy status for exam ${worklistSmid}`,
            error
          );
        },
      });
    },
    [
      canMarkAsDiscrepant,
      currentCase?.report?.smid,
      worklistSmid,
      enqueueToast,
      setReportDiscrepancyMutation,
    ]
  );

  useRecorderVoiceCommandEventsListener(({ payload }) => {
    if (shouldIgnoreVoiceCommands) {
      return;
    }

    if (!currentCase?.smid) {
      return;
    }
    switch (payload.substitution.action) {
      case 'ClearDiscrepancy':
        if (!isDiscrepancyFlagged) {
          return;
        }
        toggleMarkAsDiscrepant(false);
        break;
      case 'FlagDiscrepancy':
        if (isDiscrepancyFlagged) {
          return;
        }
        toggleMarkAsDiscrepant(true);
        break;
      default:
        break;
    }
  });

  return useMemo(
    () => ({
      shouldRenderMarkAsDiscrepantToggle,
      isDiscrepancyFlagged,
      canMarkAsDiscrepant,
      toggleMarkAsDiscrepant,
      isDisabled,
      isSettingDiscrepancy,
    }),
    [
      shouldRenderMarkAsDiscrepantToggle,
      isDiscrepancyFlagged,
      canMarkAsDiscrepant,
      toggleMarkAsDiscrepant,
      isDisabled,
      isSettingDiscrepancy,
    ]
  );
};
